import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Search, SlidersHorizontal, X, LayoutGrid, Check, Ban } from 'lucide-react';
import {
  useBookings, useAllBookings, useBookingById, type Booking, BOOKING_STATUS,
  BOOKING_STATUS_PENDING, BOOKING_STATUS_CONFIRMED,
  BOOKING_STATUS_COMPLETED, BOOKING_STATUS_CANCELLED,
} from '../../hooks/useBookings';
import { useServices, useServiceSchedule } from '../../hooks/useServices';
import { TableContent } from '../LayoutPage';
import { useBusinessContext } from '../../context/BusinessContext';
import { useUserPermissions } from '../../hooks/useUserPermissions';
import { worksAsFrontDesk } from '../../utils/frontDeskMatrix';
import { useBusinesses, type Business } from '../../hooks/useBusinessAccessType';
import { BookingDetailPanel } from './BookingDetailPanel';
import {
  useCancelRequests, useApproveCancelRequest, useRejectCancelRequest, isBookingOpsUnavailable,
} from '../../hooks/useBookingOps';
import type { CancelRequest } from '../../api/bookingOps';
import './bookings.css';
import { DateField } from '../../components/ui/DateTimeField';

// SAM-553 — a sentinel status-filter value distinct from any real status id, so the "Cancel
// requests" chip can reuse the same chip-row UI/selection state as the status chips while
// switching the list pane to an entirely different data source (the pending-request queue).
const CANCEL_REQUESTS_FILTER = 'cancel_requests';

// Booking statuses come from useBookings — see BOOKING_STATUS there for why there is exactly one
// definition and not a copy per screen.
const STATUS_DOT_CLASS: Record<number, string> = Object.fromEntries(
  BOOKING_STATUS.map((s) => [s.id, s.dotClass]),
);
const STATUS_LABEL: Record<number, string> = Object.fromEntries(
  BOOKING_STATUS.map((s) => [s.id, s.label]),
);

// Derived from BOOKING_STATUS rather than re-listed: this file used to carry its own copy of the
// id->label pairs, and it had Completed and Cancelled the wrong way round (3 is Completed, 4 is
// Cancelled), so the "Cancelled" chip filtered to completed bookings and vice versa. The same
// inversion had already been found and fixed in four other places; a hand-written fifth copy was
// always going to drift again. CHIP_ORDER only decides which statuses appear and in what order —
// the label and the dot always come from the one source.
const CHIP_ORDER = [
  BOOKING_STATUS_CONFIRMED,
  BOOKING_STATUS_PENDING,
  BOOKING_STATUS_COMPLETED,
  BOOKING_STATUS_CANCELLED,
] as const;

const FILTER_CHIPS: Array<{ id: string | null; label: string; dotClass?: string }> = [
  { id: null, label: 'All' },
  ...CHIP_ORDER.map((statusId) => {
    const status = BOOKING_STATUS.find((s) => s.id === statusId)!;
    return { id: String(status.id), label: status.label, dotClass: status.dotClass };
  }),
  // SAM-553 — pending guest-initiated cancellation requests, approved/rejected by staff.
  { id: CANCEL_REQUESTS_FILTER, label: 'Cancel requests' },
];

function startOfDay(d: Date) { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; }
function daysBetween(a: Date, b: Date) {
  const ms = startOfDay(a).getTime() - startOfDay(b).getTime();
  return Math.round(ms / 86400000);
}
function groupKey(dateStr: string): string {
  try {
    const d = new Date(dateStr);
    const today = new Date();
    const diff = daysBetween(d, today);
    if (diff < -1) return 'PAST';
    if (diff === -1) return 'YESTERDAY';
    if (diff === 0) return 'TODAY';
    if (diff === 1) return 'TOMORROW';
    if (diff <= 7) return 'THIS_WEEK';
    return 'LATER';
  } catch { return 'LATER'; }
}
function groupLabel(key: string, date: Date) {
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
  switch (key) {
    case 'TODAY': return `Today · ${date.toLocaleDateString('en-US', opts)}`;
    case 'TOMORROW': return 'Tomorrow';
    case 'YESTERDAY': return 'Yesterday';
    case 'THIS_WEEK': return 'This week';
    case 'PAST': return 'Past';
    case 'LATER': return 'Later';
    default: return key;
  }
}
const GROUP_ORDER = ['TODAY', 'TOMORROW', 'THIS_WEEK', 'LATER', 'YESTERDAY', 'PAST'];

function formatTime(dateStr: string) {
  try {
    return new Date(dateStr).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  } catch { return ''; }
}
function formatDateInline(dateStr: string) {
  try {
    return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  } catch { return ''; }
}

function todayKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const ServiceSlots: React.FC<{ serviceId: string }> = ({ serviceId }) => {
  const { data: schedule, isLoading } = useServiceSchedule(serviceId);
  const day = schedule?.availability?.[todayKey()];

  if (isLoading) return <div className="bk-slots-loading">Loading…</div>;
  if (!day?.slots?.length) return <div className="bk-slots-empty">No schedule available</div>;

  return (
    <div className="bk-slots-row">
      {day.slots.map((slot) => {
        const full = slot.bookings_count >= slot.max_bookings || slot.available === false;
        const partial = !full && slot.bookings_count > 0;
        const cls = full ? 'full' : partial ? 'partial' : 'open';
        return (
          <span key={slot.time} className={`bk-slot bk-slot-${cls}`}>
            {slot.time}
            <span className="bk-slot-count">{slot.bookings_count}/{slot.max_bookings}</span>
          </span>
        );
      })}
    </div>
  );
};

/** `embedded`: inside Activity's Bookings tab, which already says «Bookings» (SAM-1312, U12): no second title. */
export const Bookings: React.FC<{ embedded?: boolean }> = ({ embedded = false }) => {
  const { business } = useBusinessContext();
  const businessId = business?.id || null;
  const permissions = useUserPermissions();
  // Admin and the tenant Front Desk both see every business of the tenant, with a selector.
  const seesAllBusinesses = permissions.seesAllBusinesses;

  const { data: businessesData } = useBusinesses();
  const businesses: Business[] = useMemo(() => {
    const raw = businessesData as any;
    if (Array.isArray(raw)) return raw;
    if (raw?.data && Array.isArray(raw.data)) return raw.data;
    return [];
  }, [businessesData]);

  // SAM-608 — deep link: /bookings?booking=<id>[&business=<id>] opens that booking's detail
  // (from a pass in the business Activity or Arrivals). The URL follows the selection, so a
  // reload or a shared link lands on the same booking.
  const [searchParams, setSearchParams] = useSearchParams();
  const deepBookingId = searchParams.get('booking');
  const [selectedBusinessFilter, setSelectedBusinessFilter] = useState<string | null>(
    () => searchParams.get('business') || null,
  );
  const effectiveBusinessId = seesAllBusinesses ? selectedBusinessFilter : businessId;

  const { data: filteredBookings, isLoading: filteredLoading } = useBookings(effectiveBusinessId);
  const { data: allBookings, isLoading: allLoading } = useAllBookings();

  const isShowingAll = seesAllBusinesses && !selectedBusinessFilter;
  const bookings = isShowingAll ? allBookings : filteredBookings;
  const isLoading = isShowingAll ? allLoading : filteredLoading;

  const { data: services } = useServices(effectiveBusinessId);

  const deepListMatch = useMemo(
    () => (deepBookingId ? (bookings || []).find((b) => String(b.id) === deepBookingId) : undefined),
    [bookings, deepBookingId],
  );
  // Not in the loaded list (another business, or the list is still the wrong scope): read it alone.
  const { data: deepBooking } = useBookingById(deepBookingId, !isLoading && !deepListMatch);
  useEffect(() => {
    const target = deepListMatch || deepBooking;
    if (deepBookingId && target) setSelectedBookingState(target);
  }, [deepBookingId, deepListMatch, deepBooking]);

  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [selectedBooking, setSelectedBookingState] = useState<Booking | null>(null);
  const setSelectedBooking = (b: Booking | null) => {
    setSelectedBookingState(b);
    const next = new URLSearchParams(searchParams);
    if (b) next.set('booking', String(b.id));
    else next.delete('booking');
    next.delete('business');
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
  };

  // SAM-553 — "Cancel requests" chip swaps the list pane for the pending-request queue instead of
  // filtering the booking list (there is no status id for "has a pending cancel request").
  // Cancel requests (GET /bookings/cancel-requests) answers 403 for the tenant Front Desk.
  // The same for the estate group roles (SAM-1108).
  const asFrontDesk = worksAsFrontDesk(permissions);
  const filterChips = asFrontDesk ? FILTER_CHIPS.filter((c) => c.id !== CANCEL_REQUESTS_FILTER) : FILTER_CHIPS;
  const isCancelRequestsMode = !asFrontDesk && statusFilter === CANCEL_REQUESTS_FILTER;
  const cancelRequestsQuery = useCancelRequests(isCancelRequestsMode ? effectiveBusinessId : null);
  const approveCancelRequest = useApproveCancelRequest();
  const rejectCancelRequest = useRejectCancelRequest();
  const [crAction, setCrAction] = useState<null | { request: CancelRequest; type: 'approve' | 'reject' }>(null);
  const [crReason, setCrReason] = useState('');

  const activeFilterCount = useMemo(() => {
    let n = 0;
    if (statusFilter) n++;
    if (dateFrom) n++;
    if (dateTo) n++;
    return n;
  }, [statusFilter, dateFrom, dateTo]);

  const displayBookings = useMemo(() => {
    if (!bookings) return [];
    let list = bookings;
    if (statusFilter) list = list.filter((b) => String(b.status_id) === statusFilter);
    if (dateFrom) {
      const from = new Date(dateFrom);
      list = list.filter((b) => new Date(b.reservation_start) >= from);
    }
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      list = list.filter((b) => new Date(b.reservation_start) <= to);
    }
    if (searchTerm.trim()) {
      const q = searchTerm.toLowerCase();
      list = list.filter(
        (b) =>
          b.confirmation_code?.toLowerCase().includes(q) ||
          b.service_name?.toLowerCase().includes(q) ||
          b.user_name?.toLowerCase().includes(q) ||
          b.user_email?.toLowerCase().includes(q) ||
          b.notes?.toLowerCase().includes(q),
      );
    }
    return list;
  }, [bookings, searchTerm, statusFilter, dateFrom, dateTo]);

  // Group bookings by relative date
  const grouped = useMemo(() => {
    const map: Record<string, Booking[]> = {};
    displayBookings.forEach((b) => {
      const key = groupKey(b.reservation_start);
      if (!map[key]) map[key] = [];
      map[key].push(b);
    });
    return map;
  }, [displayBookings]);

  return (
    <TableContent>
      <div className={`bk-app ${selectedBooking ? 'has-selection' : ''}`}>
        {/* ===== LEFT LIST ===== */}
        <aside className="bk-list-pane">
          <header className="bk-list-header">
            <div className="bk-list-title-row">
              {!embedded && <h1 className="bk-list-title">Bookings</h1>}
              <div className="bk-title-right" style={embedded ? { marginLeft: 'auto' } : undefined}>
                {!isLoading && (bookings?.length ?? 0) > 0 && (
                  <span className="bk-list-count">{bookings?.length}</span>
                )}
                <button
                  type="button"
                  className={`bk-filter-btn ${filterOpen || activeFilterCount > 0 ? 'is-active' : ''}`}
                  onClick={() => setFilterOpen((v) => !v)}
                >
                  <SlidersHorizontal size={14} />
                  {activeFilterCount > 0 && (
                    <span className="bk-filter-badge">{activeFilterCount}</span>
                  )}
                </button>
              </div>
            </div>

            <div className="bk-search">
              <span className="bk-search-icon"><Search size={14} /></span>
              <input
                className="bk-search-input"
                placeholder="Search by guest, code, service…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {seesAllBusinesses && (
              <select
                className="bk-business-select"
                value={selectedBusinessFilter ?? ''}
                onChange={(e) => {
                  const v = e.target.value;
                  setSelectedBusinessFilter(v || null);
                  setSelectedBooking(null);
                }}
              >
                <option value="">All Businesses</option>
                {businesses.map((b) => (
                  <option key={b.id} value={b.id}>{b.name}</option>
                ))}
              </select>
            )}

            {activeFilterCount > 0 && (
              <div className="bk-active-pills">
                {statusFilter && (
                  <button type="button" className="bk-active-pill" onClick={() => setStatusFilter(null)}>
                    <span className={`bk-chip-dot ${FILTER_CHIPS.find(c => c.id === statusFilter)?.dotClass ?? ''}`} />
                    {STATUS_LABEL[Number(statusFilter)]}
                    <X size={10} />
                  </button>
                )}
                {dateFrom && (
                  <button type="button" className="bk-active-pill" onClick={() => setDateFrom('')}>
                    From {dateFrom} <X size={10} />
                  </button>
                )}
                {dateTo && (
                  <button type="button" className="bk-active-pill" onClick={() => setDateTo('')}>
                    To {dateTo} <X size={10} />
                  </button>
                )}
              </div>
            )}

            {filterOpen && (
              <div className="bk-filter-panel">
                <div className="bk-filter-section">
                  <div className="bk-filter-label">Status</div>
                  <div className="bk-chips">
                    {filterChips.map((chip) => (
                      <button
                        key={chip.label}
                        type="button"
                        className={`bk-chip ${statusFilter === chip.id ? 'is-active' : ''}`}
                        onClick={() => setStatusFilter(chip.id)}
                      >
                        {chip.dotClass && <span className={`bk-chip-dot ${chip.dotClass}`} />}
                        {chip.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="bk-filter-section">
                  <div className="bk-filter-label">Date Range</div>
                  <div className="bk-date-row">
                    <DateField ariaLabel="From date" placeholder="From" className="flex-1" value={dateFrom} onChange={setDateFrom} />
                    <span className="bk-date-sep">to</span>
                    <DateField ariaLabel="To date" placeholder="To" className="flex-1" value={dateTo} onChange={setDateTo} />
                  </div>
                </div>

                {activeFilterCount > 0 && (
                  <button
                    type="button"
                    className="bk-clear-filters"
                    onClick={() => { setStatusFilter(null); setDateFrom(''); setDateTo(''); }}
                  >
                    Clear all filters
                  </button>
                )}
              </div>
            )}
          </header>

          <div className="bk-list-scroll">
            {isCancelRequestsMode ? (
              cancelRequestsQuery.isLoading ? (
                <div className="bk-list-empty">Loading cancellation requests…</div>
              ) : isBookingOpsUnavailable(cancelRequestsQuery.error) ? (
                <div className="bk-list-empty">Cancel requests queue is not available yet.</div>
              ) : cancelRequestsQuery.isError ? (
                <div className="bk-list-empty">Could not load cancellation requests.</div>
              ) : !cancelRequestsQuery.data || cancelRequestsQuery.data.length === 0 ? (
                <div className="bk-list-empty">No pending cancellation requests</div>
              ) : (
                cancelRequestsQuery.data.map((request) => {
                  const customer = request.user_name || request.user_email || `Booking ${request.booking_id.substring(0, 8)}`;
                  return (
                    <div key={request.id} className="bk-item bk-cr-item">
                      <div className="bk-item-content">
                        <div className="bk-item-top">
                          <span className="bk-item-customer">{customer}</span>
                        </div>
                        <div className="bk-item-meta">
                          <span className="bk-item-service">
                            {request.service_name || 'Service'}
                            {request.reservation_start && ` · ${formatDateInline(request.reservation_start)} ${formatTime(request.reservation_start)}`}
                          </span>
                        </div>
                        {request.reason && <div className="bk-item-code">Reason: {request.reason}</div>}
                        <div className="bk-cr-actions">
                          <button
                            type="button"
                            className="bk-btn bk-btn-secondary"
                            onClick={() => { setCrAction({ request, type: 'reject' }); setCrReason(''); }}
                          >
                            <Ban size={13} /> Reject
                          </button>
                          <button
                            type="button"
                            className="bk-btn bk-btn-primary"
                            onClick={() => { setCrAction({ request, type: 'approve' }); setCrReason(''); }}
                          >
                            <Check size={13} /> Approve
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })
              )
            ) : isLoading ? (
              <div className="bk-list-empty">Loading bookings…</div>
            ) : displayBookings.length === 0 ? (
              <div className="bk-list-empty">No bookings found</div>
            ) : (
              GROUP_ORDER.filter((k) => grouped[k]?.length).map((key) => (
                <div key={key}>
                  <div className="bk-group-label">
                    {groupLabel(key, key === 'TODAY' ? new Date() : new Date(grouped[key][0].reservation_start))}
                  </div>
                  {grouped[key].map((booking) => {
                    const isSelected = selectedBooking?.id === booking.id;
                    const totalAmount = Array.isArray(booking.booking_options)
                      ? booking.booking_options.reduce((s: number, o: any) => s + (o.total_amount || 0), 0)
                      : 0;
                    const currency = Array.isArray(booking.booking_options) && booking.booking_options.length > 0
                      ? (booking.booking_options[0].currency || 'USD')
                      : 'USD';
                    const customer = booking.user_name || booking.user_email || `Guest ${booking.main_user_id}`;

                    return (
                      <div
                        key={booking.id}
                        className={`bk-item ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => setSelectedBooking(booking)}
                      >
                        <span className={`bk-item-status ${STATUS_DOT_CLASS[booking.status_id] || ''}`} />
                        <div className="bk-item-content">
                          <div className="bk-item-top">
                            <span className="bk-item-customer">{customer}</span>
                            {totalAmount > 0 && (
                              <span className="bk-item-amount">${totalAmount.toFixed(2)}</span>
                            )}
                          </div>
                          <div className="bk-item-meta">
                            <span className="bk-item-service">
                              {booking.service_name || 'Service'}
                              {booking.business_name && ` · ${booking.business_name}`}
                            </span>
                            <span className="bk-item-time">
                              {key === 'TODAY' || key === 'TOMORROW' || key === 'YESTERDAY'
                                ? formatTime(booking.reservation_start)
                                : `${formatDateInline(booking.reservation_start)} · ${formatTime(booking.reservation_start)}`}
                            </span>
                          </div>
                          <div className="bk-item-code">
                            {booking.confirmation_code || booking.id.substring(0, 12)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              ))
            )}
          </div>
        </aside>

        {/* ===== RIGHT DETAIL ===== */}
        <main className="bk-detail-pane">
          {selectedBooking ? (
            <BookingDetailPanel
              key={selectedBooking.id}
              booking={selectedBooking}
              onClose={() => setSelectedBooking(null)}
            />
          ) : !effectiveBusinessId && seesAllBusinesses ? (
            <div className="bk-empty">
              <LayoutGrid size={40} className="bk-empty-icon" />
              <div className="bk-empty-title">Select a business</div>
              <div className="bk-empty-sub">Choose a business to see today's availability</div>
            </div>
          ) : (
            <div className="bk-overview">
              <div className="bk-overview-header">
                <div className="bk-overview-title">Today's Availability</div>
                <div className="bk-overview-sub">
                  {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
                </div>
              </div>
              <div className="bk-overview-body">
                {!services || services.length === 0 ? (
                  <div className="bk-empty" style={{ height: 'auto', padding: '48px 0' }}>
                    <div className="bk-empty-title">No services found</div>
                    <div className="bk-empty-sub">Select a booking from the left to view details</div>
                  </div>
                ) : (
                  <>
                    <div className="bk-overview-count">Services · {services.length}</div>
                    {services.map((svc) => (
                      <div key={svc.id} className="bk-svc-card">
                        <div className="bk-svc-card-header">
                          {(svc.logo_url || svc.image_url) && (
                            <img
                              src={svc.logo_url || svc.image_url}
                              alt=""
                              className="bk-svc-logo"
                            />
                          )}
                          <div className="bk-svc-card-info">
                            <div className="bk-svc-name">{svc.name_en || svc.name}</div>
                            {svc.status && (
                              <div className={`bk-svc-status bk-svc-status-${svc.status}`}>{svc.status}</div>
                            )}
                          </div>
                        </div>
                        <ServiceSlots serviceId={svc.id} />
                      </div>
                    ))}
                  </>
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* SAM-553 — approve/reject a pending cancellation request. */}
      {crAction && (
        <div className="bk-confirm-overlay" onClick={(e) => e.target === e.currentTarget && setCrAction(null)}>
          <div className="bk-confirm-box">
            <div className="bk-confirm-title">
              {crAction.type === 'approve' ? 'Approve cancellation request?' : 'Reject cancellation request?'}
            </div>
            <div className="bk-confirm-body">
              {crAction.type === 'approve'
                ? 'This cancels the booking and notifies the guest.'
                : 'The booking stays active and the guest is notified their request was denied.'}
              <label className="bk-field-label" htmlFor="bk-cr-reason">Reason</label>
              <textarea
                id="bk-cr-reason"
                className="bk-notes-box"
                placeholder="Why is this request being decided this way?"
                value={crReason}
                onChange={(e) => setCrReason(e.target.value)}
              />
            </div>
            <div className="bk-confirm-footer">
              <button className="bk-btn bk-btn-secondary" type="button" onClick={() => setCrAction(null)}>Back</button>
              <button
                className={`bk-btn ${crAction.type === 'approve' ? 'bk-btn-primary' : 'bk-btn-danger'}`}
                type="button"
                disabled={!crReason.trim() || approveCancelRequest.isPending || rejectCancelRequest.isPending}
                onClick={() => {
                  const mutation = crAction.type === 'approve' ? approveCancelRequest : rejectCancelRequest;
                  mutation.mutate(
                    { bookingId: crAction.request.booking_id, reason: crReason.trim() },
                    { onSuccess: () => setCrAction(null) },
                  );
                }}
              >
                {crAction.type === 'approve' ? 'Approve' : 'Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </TableContent>
  );
};

export default Bookings;
